<template>
  <div class="container mt-4">

    <!-- Documentation Header -->
    <h1>🎛 RadioFieldDemo</h1>
    <p>
      A Vue component demonstrating the usage of
      <strong>RadioField</strong> and <strong>ImgRadioField</strong>
      for text-based and image/icon-based radio options.
    </p>
    <hr />

    <!-- Features List -->
    <h2>✨ Features</h2>
    <ul>
      <li>🔘 Standard radio buttons with customizable labels (<strong>RadioField</strong>)</li>
      <li>🖼 Image or icon-based radio buttons (<strong>ImgRadioField</strong>)</li>
      <li>🔄 Two-way binding with <code>v-model</code></li>
      <li>🧩 Supports custom <code>valueField</code> and <code>textField</code></li>
      <li>🚫 Disabled state support</li>
      <li>📦 Works with dynamic option arrays from <code>computed</code> or <code>data</code></li>
      <li>📱 Responsive layout with Bootstrap grid</li>
    </ul>
    <hr />

    <!-- Usage Examples -->
    <h2>💻 Usage Example</h2>

    <h3>Standard RadioField</h3>
    <pre class="p-3 mb-2 bg-white text-dark">
      <code>
        &lt;RadioField
          :options="options"
          v-model="form.type_1"
          :value-field="'type'"
          :text-field="'label'"
          class="input-field"
        /&gt;
      </code>
    </pre>

    <h3>ImgRadioField (Icon / Image)</h3>
    <pre class="p-3 mb-2 bg-white text-dark">
      <code>
        &lt;ImgRadioField
          :options="options_2"
          v-model="form.type_2"
          :value-field="'type'"
          :text-field="'label'"
          class="input-field"
        /&gt;
      </code>
    </pre>

    <hr />

    <!-- Props Table -->
    <h2>⚙ Props</h2>
    <ul>
      <li><strong>v-model</strong> – binds the selected value to a parent property</li>
      <li><strong>options</strong> – array of objects defining radio choices</li>
      <li><strong>valueField</strong> – key in the option object used as the radio value</li>
      <li><strong>textField</strong> – key in the option object used as the label (ignored in ImgRadioField if using image/icon)</li>
      <li><strong>disabled</strong> – disables the input if true</li>
    </ul>

    <hr />

    <!-- Example Data -->
    <h2>📂 Example Data</h2>
    <pre class="p-3 mb-2 bg-white text-dark">
      <code>
        form: {
          type_1: 1,
          type_2: 2
        },

        options() {
          return [
            { type: 1, label: 'text 1' },
            { type: 2, label: 'text 2' }
          ];
        },

        options_2() {
          return [
            { type: 1, icon: 'bi bi-bank fs-1' },
            { type: 2, image: '/src/components/billing/subscribe/img/128px-Stripe_Logo.png' }
          ];
        }
      </code>
    </pre>

    <hr />

    <!-- Notes -->
    <h2>📌 Notes</h2>
    <ul>
      <li>✅ Automatically emits <code>update:modelValue</code> for seamless <code>v-model</code> binding</li>
      <li>✅ Each radio input has a unique ID to prevent conflicts</li>
      <li>✅ Supports both static and dynamically generated option arrays</li>
      <li>✅ Ideal for forms, admin panels, or visual selection inputs</li>
    </ul>

    <div class="row mt-4">
      <div class="col-6">
        <h4>Live Standard RadioField</h4>
        <RadioField
            :options="options"
            v-model="form.type_1"
            :value-field="'type'"
            :text-field="'label'"
            class="input-field"
        />
      </div>

      <div class="col-6">
        <h4>Live ImgRadioField</h4>
        <ImgRadioField
            :options="options_2"
            v-model="form.type_2"
            :value-field="'type'"
            :text-field="'label'"
            class="input-field"
        />
      </div>
    </div>
  </div>
</template>

<script>
import RadioField from "@/components/common/radio-field/RadioField.vue";
import ImgRadioField from "@/components/common/img-radio-field/ImgRadioField.vue";

export default {
  name: 'RadioFieldDemo',
  components: { RadioField, ImgRadioField },

  data() {
    return {
      form: {
        type_1: 1,
        type_2: 2
      }
    }
  },

  computed: {
    options() {
      return [
        { type: 1, label: 'text 1' },
        { type: 2, label: 'text 2' }
      ];
    },
    options_2() {
      return [
        { type: 1, icon: 'bi bi-bank fs-1' },
        { type: 2, image: '/src/components/billing/subscribe/img/128px-Stripe_Logo.png' }
      ];
    }
  }
}
</script>

<style scoped>
.input-field {
  margin-bottom: 1rem;
}
</style>